import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export const ManualPaymentDialog = ({ isOpen, onClose, amount, title, endpoint, extra = {} }) => {
  const { token } = useAuth();
  const [methods, setMethods] = useState({});
  const [method, setMethod] = useState('');
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMethod(''); setDone(false); setName(''); setReference(''); setMethods({});
    axios.get(`${API}/subscription/payment/methods`)
      .then(({ data }) => setMethods(data.methods || {}))
      .catch(() => toast.error('No se pudieron cargar los métodos de pago'));
  }, [isOpen]);

  const submit = async () => {
    if (!name.trim() || !reference.trim()) {
      toast.error('Indica el titular y la referencia de tu pago');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}${endpoint}`, {
        ...extra, payment_method: method,
        zelle_name: name.trim(), zelle_confirmation: reference.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDone(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo registrar el pago');
    } finally {
      setSending(false);
    }
  };

  const selected = methods[method];
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white">
        <DialogHeader><DialogTitle>{title} · ${Number(amount).toFixed(2)} USD</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-4">
            <p>Solicitud recibida. Verificaremos el pago en la cuenta receptora antes de activar la compra.</p>
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">Elige cómo pagar. La activación requiere verificación manual.</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(methods).map(([key, info]) => (
                <Button key={key} type="button" variant={method === key ? 'default' : 'outline'} onClick={() => setMethod(key)}
                  data-testid={`payment-method-${key}`}>
                  {info.label}
                </Button>
              ))}
            </div>
            {Object.keys(methods).length === 0 && <p className="text-amber-300">No hay métodos de cobro configurados.</p>}
            {selected && <>
              <div className="rounded-lg bg-slate-800 p-4 space-y-2">
                <p>Envía exactamente <strong>${Number(amount).toFixed(2)} USD</strong> a {selected.label}:</p>
                <p className="break-all font-semibold">{selected.destination}</p>
                {method === 'paypal' && <a className="text-sky-300 underline" href={selected.destination} target="_blank" rel="noopener noreferrer">Abrir enlace de PayPal</a>}
                {method === 'cash_app' && <a className="text-sky-300 underline" href={selected.url} target="_blank" rel="noopener noreferrer">Abrir perfil de Cash App</a>}
                <p className="text-xs text-slate-300">Verifica el destinatario en la aplicación antes de enviar. Guarda tu recibo.</p>
              </div>
              <Label htmlFor="manual-payer">Nombre del titular que envió el pago</Label>
              <Input id="manual-payer" data-testid="payment-payer-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-800" />
              <Label htmlFor="manual-reference">Referencia o ID de transacción</Label>
              <Input id="manual-reference" data-testid="payment-reference" maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} className="bg-slate-800" />
              <Button onClick={submit} disabled={sending || !name.trim() || !reference.trim()} className="w-full" data-testid="payment-submit-btn">
                {sending ? 'Enviando…' : 'Ya pagué: solicitar verificación'}
              </Button>
            </>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PaymentMethodModal = ({ isOpen, onClose, plan, billingCycle }) => (
  <ManualPaymentDialog
    isOpen={isOpen} onClose={onClose}
    title={plan?.name || 'Suscripción'}
    amount={billingCycle === 'yearly' ? plan?.price_yearly : plan?.price}
    endpoint="/subscription/payment/zelle"
    extra={{ plan_id: plan?.id, billing_cycle: billingCycle }}
  />
);

export default PaymentMethodModal;
